import styles from './WithdrawList.module.scss';
import { WithdrawTable } from './components/WithdrawTable/WithdrawTable';
import React, { KeyboardEvent, useState } from 'react';
import { useGetWithdrawRequests } from '../../hooks/useGetWithdrawRequests';
import { GreenButton } from '../Shared/GreenButton/GreenButton';
import { exportToExcel } from '../../api/withdraw';
import 'react-multi-date-picker/styles/backgrounds/bg-dark.css';
import 'react-multi-date-picker/styles/colors/teal.css';
import DatePicker from 'react-multi-date-picker';
import format from 'date-fns/format';

import { PAGE_OPTIONS } from '../../constants';
import { Loader } from '../Shared/Loader/Loader';
import { Paging } from '../Shared/Paging/Paging';
import { EWithdrawStatus } from '../../types/EWithdrawStatus';
import Select from 'react-select';

type OptionType = {
    value: string | undefined;
    label: string;
};

type OptionBooleanType = {
    value: boolean | undefined;
    label: string;
};

const verifiedOptions: OptionBooleanType[] = [
    { value: undefined, label: 'All' },
    { value: true, label: 'Verified' },
    { value: false, label: 'Not Verified' },
];

const statusOptions: OptionType[] = [
    { value: undefined, label: 'All Statuses' },
    { value: EWithdrawStatus.ERROR, label: 'Error' },
    { value: EWithdrawStatus.IN_PROGRESS, label: 'In Progress' },
    { value: EWithdrawStatus.APPROVED, label: 'Approved' },
    { value: EWithdrawStatus.PROCESSING, label: 'Processing' },
    { value: EWithdrawStatus.SUCCESS, label: 'Success' },
];

export const WithdrawList = () => {
    const [pageSize, setPageSize] = useState(PAGE_OPTIONS[0]);
    const [pageNumber, setPageNumber] = useState(1);
    const [search, setSearch] = useState('');
    const [createdFrom, setCreatedFrom] = useState<string | undefined>(undefined);
    const [createdTo, setCreatedTo] = useState<string | undefined>(undefined);

    const [status, setStatus] = useState<OptionType | undefined>(statusOptions[0]);
    const [isVerified, setIsVerified] = useState<OptionBooleanType | undefined>(verifiedOptions[0]);

    const { withdrawRequestsData, isLoading, onSelect, selectAll, refetch } = useGetWithdrawRequests({
        pageSize,
        pageNumber,
        searchQuery: search,
        statuses: status ? status.value : undefined,
        isVerified: isVerified ? isVerified.value : undefined,
        createdFrom,
        createdTo,
    });

    const onImport = () => {
        console.log('onImport');
    };

    const onExport = async () => {
        await exportToExcel({
            pageSize: 1000,
            pageNumber: 1,
            searchQuery: search ?? undefined,
            statuses: status ? status.value : undefined,
            isVerified: isVerified ? isVerified.value : undefined,
            createdTo,
            createdFrom,
        });
    };

    const onSearchChange = (event: KeyboardEvent<HTMLInputElement>) => {
        const search = (event.target as HTMLInputElement).value;
        if (event.key === 'Enter') {
            setSearch(search);
            setPageNumber(1);
        }
    };

    const changePageSize = (size: number) => {
        setPageSize(size);
        setPageNumber(1);
    };

    const onChangeStatus = (option: Option | null) => {
        setStatus(option);
    };

    const onChangeVerifiedStatus = (option: Option | null) => {
        setIsVerified(option);
    };

    const onChangeDate = (dates: string[]) => {
        if (!dates || dates.length < 2) {
            setCreatedFrom(undefined);
            setCreatedTo(undefined);
        }
        if (dates && dates[0]) {
            const formatedDateFrom = format(new Date(dates[0]), 'yyyy-MM-dd') + 'T00:00:00.000Z';
            setCreatedFrom(formatedDateFrom);
        }
        if (dates && dates[1]) {
            const formatedDateTo = format(new Date(dates[1]), 'yyyy-MM-dd') + 'T23:59:59.000Z';

            setCreatedTo(formatedDateTo);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.title}>Requests</div>
            <div className={styles.row}>
                <input type="text" onKeyDown={onSearchChange} className={styles.search} placeholder="Search"></input>
                <Select
                    classNames={{
                        control: () => styles.control,
                        menu: () => styles.controlMenu,
                    }}
                    defaultValue={isVerified}
                    onChange={onChangeVerifiedStatus}
                    options={verifiedOptions}
                />
                <Select
                    classNames={{
                        control: () => styles.control,
                        menu: () => styles.controlMenu,
                    }}
                    defaultValue={status}
                    onChange={onChangeStatus}
                    options={statusOptions}
                />
                <DatePicker
                    editableDateInputs={true}
                    range
                    placeholder={'Select date'}
                    inputClass={styles.control}
                    moveRangeOnFirstSelection={false}
                    className="bg-dark teal"
                    onChange={onChangeDate}
                />
                <GreenButton outline mini onClick={onImport}>
                    Import data
                </GreenButton>
                <GreenButton mini onClick={onExport}>
                    <div className={styles.buttonRow}>
                        <div className={styles.buttonText}>Export to Excel</div>
                    </div>
                </GreenButton>
            </div>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <WithdrawTable
                        data={withdrawRequestsData?.entities}
                        selectRequest={onSelect}
                        selectAll={selectAll}
                        refetch={refetch}
                    />
                    <Paging
                        totalRecords={withdrawRequestsData?.total ?? 1}
                        pageSize={pageSize}
                        pageNumber={pageNumber}
                        changePage={setPageNumber}
                        changePageSize={changePageSize}
                    />
                </>
            )}
        </div>
    );
};
